<template>
  <div id="app">
    <scene id="scene" v-bind:product="product"></scene>
    <order-form
      v-if="showOrderForm"
      v-bind:productSummary="productSummary"
      v-on:alert="onOrderFormAlert"
      v-on:close="showOrderForm = false"
      v-on:confirm="confirmOrder"
      id="order-form"></order-form>
    <div id="configuration-panel">
      <component-type-selector id="component-type-selector"
        v-bind:productType="product.type"
        v-bind:componentTypeDict="productSummary.components"
        v-bind:targetComponentType="targetComponent && targetComponent.type"
        v-bind:availableComponentTypes="product.availableComponentTypes"
        v-on:component-type-selected="changeTargetComponentType"
        ></component-type-selector>
      <div id="component-detail-panel-container">
        <!-- combination component panel -->
        <div class="component-detail-panel"
          v-if="targetComponent && targetComponent.type == 'combination' && availableCombinationTypes.length > 0"
          v-bind:show-second-level-selector="availableCombinationTypes.length > 1"
          >
          <div class="second-level-selector-container">
            <combination-component-type-selector
              v-if="availableCombinationTypes.length > 1"
              v-bind:combinationTypes="availableCombinationTypes"
              v-bind:value="targetCombinationType"
              v-on:combination-type-selected='changeTargetCombinationType'>
            </combination-component-type-selector>
          </div>
          <div class="component-customization-panel-container">
            <div class="component-customization-panel">
              <component-selector
                mode="combination"
                v-bind:componentType="targetCombinationType"
                v-bind:componentsInUse="productSummary.components"
                v-on:component-selected="componentSelected">
              </component-selector>
              <div v-if="targetComponent.variations">
                <variation-selector
                  v-if="targetComponent.component.isCombination"
                  v-for="variation in targetComponent.variations"
                  v-bind:name="variation.name"
                  v-bind:options="variation.options"
                  v-bind:id="variation.id"
                  v-bind:value="variation.value"
                  v-on:variation-selected="variationSelected">
                </variation-selector>
              </div>
            </div>
          </div>
        </div>
        <!-- ordinary component panel -->
        <div class="component-detail-panel"
          v-for="type in productSummary.components"
          v-bind:show-second-level-selector="type.components.length > 1"
          v-if="targetComponent && targetComponent.type == type.id">
          <div class="second-level-selector-container">
            <component-position-selector
              v-bind:componentType="type.id"
              v-bind:componentPositions="type.components"
              v-bind:value="targetComponent.index"
              v-on:position-selected="changeTargetComponent">
            </component-position-selector>
          </div>
          <div class="component-customization-panel-container">
            <div class="component-customization-panel">
              <component-selector
                v-if="targetComponent"
                v-bind:isRequired = "product.manifest[type.id].min > 0"
                v-bind:componentType="targetComponent.type"
                v-bind:index="targetComponent.index"
                v-bind:value="targetComponent.id"
                v-on:component-selected="componentSelected">
              </component-selector>
              <div v-if="targetComponent.variations">
                <variation-selector
                  v-if="!targetComponent.component.isCombination"
                  v-for="variation in targetComponent.variations"
                  v-bind:name="variation.name"
                  v-bind:options="variation.options"
                  v-bind:id="variation.id"
                  v-bind:value="variation.value"
                  v-on:variation-selected="variationSelected">
                </variation-selector>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="product-info-panel">
        <div id="product-dimension">
          <div v-if="product.dimensions.seatHeight > 0">
            <span>{{ product.dimensions.seatDepth }}mm座深 / </span>
            <span>{{ product.dimensions.seatHeight }}mm座高</span>
          </div>
          <div v-if="product.dimensions.height > 0">{{ product.dimensions.depth }}mm長 x {{ product.dimensions.width }}mm寬 x {{ product.dimensions.height }}mm高</div>
        </div>
        <div id="total-price">NT$ {{ productSummary.price | priceTag }}</div>
        <div id="order-button" v-on:click="order">訂購</div>
      </div>
    </div>
    <div id="component-photo-panel-container">
      <component-photo-panel
        id="component-photo-panel"
        v-bind:productSummary="productSummary"
        v-on:component-photo-click="openLightbox">
      </component-photo-panel>
    </div>
    <div hidden id="random-button" v-on:click="randomProduct">好手氣</div>
    <lightbox v-if="lightbox.show" v-bind:photoUrl="lightbox.photoUrl" @close="closeLightbox"></lightbox>
    <modal v-if="modal.show" v-bind:mode="modal.mode" @close="closeModal">
      <h3 slot="header">{{ modal.header }}</h3>
      <span slot="body">{{ modal.body }}</span>
    </modal>
  </div>
</template>

<script>
import Vue from 'vue';
import scene from 'components/scene';
import componentTypeSelector from 'components/component-type-selector';
import componentPositionSelector from 'components/component-position-selector';
import combinationComponentTypeSelector from
  'components/combination-component-type-selector';
import componentSelector from 'components/component-selector';
import variationSelector from 'components/variation-selector';
import orderForm from 'components/order-form';
import lightbox from 'components/lightbox';
import componentPhotoPanel from 'components/component-photo-panel';
import listView from 'components/list-view';
import modal from 'components/modal';
import Product from 'modules/product';
import THREEService from 'services/three-js-service';
import ProductDatabase from 'services/product-database';
import l10n from 'services/l10n-service';
import Utils from 'utils/utils';

export default {
  name: 'app',
  data() {
    return {
      modal: {
        deferred: null,
        show: false,
        mode: 'alert',
        header: '',
        body: ''
      },
      lightbox: {
        show: false,
        photoUrl: null
      },
      showOrderForm: false,
      get availableCombinationTypes() {
        if (this.product) {
          switch (this.product.type) {
            case Product.Type.Chair:
              return ['seat', 'center_chair_leg'];
            case Product.Type.Table:
              return ['center_chair_leg'];
            default:
              return [];
          }
        } else {
          return [];
        }
      },
      targetCombinationType: null,
      targetComponent: null,
      productSummary: {},
      productWatchers: null,
      product: null
    };
  },
  components: {
    scene,
    componentTypeSelector,
    combinationComponentTypeSelector,
    componentPositionSelector,
    componentSelector,
    variationSelector,
    componentPhotoPanel,
    lightbox,
    listView,
    modal,
    orderForm
  },
  watch: {
    product: {
      handler() {
        this.refreshProductSummary();
        this.targetComponent = null;

        this.changeTargetComponentType(this.product.components.root.type);
        this.targetCombinationType = this.availableCombinationTypes.length ?
          this.availableCombinationTypes[0] : null;
        this.refreshProductChangeWatchers();
      }
    },
    targetComponent() {
      this.refreshTargetComponent();
      this.refreshComponentVariation();
    }
  },
  filters: {
    priceTag: Utils.priceTag
  },
  methods: {
    onProductChange() {
      this.refreshProductSummary();
      this.refreshTargetComponent();
      this.refreshComponentVariation();
    },
    refreshProductChangeWatchers() {
      // Unregister existing watchers.
      if (this.productWatchers) {
        this.productWatchers.forEach((productWatcher) => {
          productWatcher();
        });
        this.productWatchers = null;
      }

      // Register new watchers.
      if (this.product) {
        this.productWatchers = [];
        Object.keys(this.product.components).forEach((componentType) => {
          this.productWatchers.push(this.$watch(
            `product.components.${componentType}`, this.onProductChange));
        });
      }
    },
    confirmComponentSelection(confirmMsg) {
      return this.openModal('', confirmMsg, 'confirm').promise;
    },
    componentSelected(componentType, index, componendId) {
      if (componendId) {
        this.product.replaceComponent(componentType, index, componendId,
          this.confirmComponentSelection);
      } else {
        this.product.removeComponent(componentType, index,
          this.confirmComponentSelection);
      }
    },
    variationSelected(variationName, optionValue) {
      console.log(`variation selected ${variationName}, ${optionValue}`);
      const curComponent = this.targetComponent.component;

      if (curComponent) {
        curComponent.setVariation(variationName, optionValue);
      }

      // XXX: Manually do refrsh as we do not watch for variation changes.
      this.refreshProductSummary();
      // XXX: call this line manually to refresh the selector
      this.refreshComponentVariation();
    },
    refreshProductSummary() {
      if (!this.product) {
        this.productSummary = {};
        return;
      }

      const productSummary = {
        components: {}
      };
      let totalPrice = 0;

      // Use manifest to generate the required component list.
      Object.keys(this.product.manifest).forEach((componentType) => {
        const componetManifestOfType = this.product.manifest[componentType];
        const componentsOfType = this.product.components[componentType];
        const components = [];

        for (let i = 0; i < componetManifestOfType.max; i += 1) {
          const component = componentsOfType[i];
          let info = '無';
          const variationValues = {};
          let componentPrice = 0;

          if (component) {
            let variationPrice = 0;
            const variations = component.variations;

            info = component.name;
            Object.keys(variations).forEach((variationName) => {
              const variation = variations[variationName];
              const value = component.getVariation(variationName);
              const curOption = variation.options.find((option) => {
                return option.value === value;
              });

              info = `${info} - ${curOption.name}`;
              variationPrice += (curOption.price || 0);

              variationValues[variationName] = value;
            });
            componentPrice = component.data.price + variationPrice;
            totalPrice += componentPrice;
          }

          components.push({
            id: component && component.id,
            info,
            variationValues,
            index: i,
            price: componentPrice
          });
        }

        productSummary.components[componentType] = {
          title: l10n.get(componentType),
          id: componentType,
          components
        };
        productSummary.price = totalPrice;
      });

      this.productSummary = productSummary;
    },
    refreshComponentVariation() {
      if (!this.targetComponent) {
        return;
      }

      const component = this.targetComponent.component;
      const variations = component ?
        JSON.parse(JSON.stringify(component.variations)) : null;

      if (variations) {
        Object.keys(variations).forEach((variationName) => {
          variations[variationName].value = null;
        });
      }

      this.targetComponent.variations = variations;

      // XXX: Vue components won't update unless there are prop changes.
      // Update the variation value later to force update the selector.
      if (variations) {
        Vue.nextTick(() => {
          Object.keys(variations).forEach((variationName) => {
            variations[variationName].value =
              component.getVariation(variationName);
          });
        });
      }
    },
    refreshTargetComponent() {
      if (!this.targetComponent) {
        return;
      }

      let component;
      if (this.targetComponent.type === 'combination') {
        component = this.product.components[this.targetCombinationType][0];
      } else {
        const index = this.targetComponent.index;
        const componentType = this.targetComponent.type;
        component = this.product.components[componentType][index];
      }

      this.targetComponent.component = component;
      this.targetComponent.id = component ? component.id : null;
    },
    randomProduct() {
      if (Math.random() > 0.5) {
        this.product = new Product(Product.Type.Chair);
      } else {
        this.product = new Product(Product.Type.Table);
      }
    },
    order() {
      this.showOrderForm = true;
    },
    onOrderFormAlert(msg) {
      this.openModal('', msg);
    },
    confirmOrder(name, tel, email, notes, productCount) {
      Promise.all([
        import('services/order-generator'),
        import('services/trello-service')
      ]).then(([{ default: OrderGenerator }, { default: TrelloService }]) => {
        const { title, summary } = OrderGenerator.generate(name, tel, email,
         notes, this.productSummary, productCount);
        TrelloService.createCardWithoutCredential(title, summary);
      });
      this.openModal('', '訂單已送出，我們會盡快與您聯繫！');
    },
    openModal(header, body, mode) {
      const deferred = {
        resolve: () => {}
      };
      deferred.promise = new Promise((resolve) => {
        deferred.resolve = resolve;
      });

      mode = mode || 'alert';

      this.modal.header = header;
      this.modal.body = body;
      this.modal.show = true;
      this.modal.mode = mode;
      this.modal.deferred = deferred;

      return deferred;
    },
    closeModal(isOk) {
      this.modal.show = false;
      this.modal.header = '';
      this.modal.body = '';
      this.modal.deferred.resolve(isOk);
      this.modal.deferred = null;
    },
    openLightbox(photoUrl) {
      this.lightbox.photoUrl = photoUrl;
      this.lightbox.show = true;
    },
    closeLightbox() {
      this.lightbox.photoUrl = '';
      this.lightbox.show = false;
    },
    changeTargetCombinationType(combinationType) {
      this.targetCombinationType = combinationType;
      // this is used to force update the variation selector
      if (this.productSummary.components[combinationType]) {
        this.changeTargetComponent('combination', 0);
      } else {
        this.targetComponent = null;
      }
    },
    changeTargetComponentType(componentType) {
      this.changeTargetComponent(componentType, 0);
    },
    changeTargetComponent(componentType, index) {
      console.log(`change target component ${index}`);
      this.targetComponent = {
        type: componentType,
        index,
        id: null,
        component: null,
        variations: null
      };
    },
    updateProduct(isHashChange) {
      let productJson;
      if (location.hash) {
        const productId = location.hash.substr(1);
        productJson = ProductDatabase.getProduct(productId);
      }

      if (productJson) {
        if (isHashChange) {
          this.openModal('', '您目前的修改將會遺失，請問要繼續嗎？', 'confirm').promise
          .then((isOk) => {
            if (isOk) {
              this.product = new Product('', productJson);
            }
          });
        } else {
          this.product = new Product('', productJson);
        }
      } else if (!this.product) {
        this.randomProduct();
      }
    }
  },
  created() {
    this.updateProduct();
    window.addEventListener('hashchange', this.updateProduct.bind(this, true));
    window.onbeforeunload = () => { return true; };
  },
  mounted() {
    // Enable obj loading after the UI has been created.
    THREEService.enableObjLoading();
  }
};
</script>

<style>
body {
  margin: 0;
  padding: 0;
}

#app {
  position: relative;
  font-family: 'メイリオ', '微軟正黑體', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: black;
  background: #f2f2f2;
  height: 100%;
}

#scene {
  width: calc(100% - 470px);
  height: calc(100% - 70px);
}

#configuration-panel {
  position: absolute;
  width: 450px;
  right: 20px;
  top: 10px;
  bottom: 20px;
}

#component-type-selector {
  width: 100%;
  height: 100px;
}

#component-detail-panel-container {
  height: calc(100% - 210px);
  position: relative;
  background: white;
  box-shadow: 1px 1px 10px silver;
  -moz-box-shadow: 1px 1px 10px silver;
  -webkit-box-shadow: 1px 1px 10px silver;
}

.component-detail-panel {
  position: absolute;
  width: 100%;
  height: 100%;
  text-align: left;
  padding-top: 10px;
}

.second-level-selector-container {
  height: 60px;
  display: none;
}

.component-detail-panel[show-second-level-selector] .second-level-selector-container {
  display: block;
}

.component-customization-panel-container {
  height: calc(100% - 10px);
  overflow-y: auto;
}

.component-detail-panel[show-second-level-selector] .component-customization-panel-container {
  height: calc(100% - 70px);
}

#random-button {
  position: absolute;
  top: 50px;
  left: 50px;
  z-index: 10;
  border: 1px solid #2c3e50;
  padding: 5px 10px;
  cursor: pointer;
  user-select: none;
}

#random-button:hover {
  border: 1px solid #2c3e50;
  background: #2c3e50;
  color: white;
}

#product-info-panel {
  position: absolute;
  width: 100%;
  bottom: 0;
}

#total-price {
  position: absolute;
  font-size: 24px;
  font-weight: bold;
  bottom: 55px;
  right: 0;
  box-sizing: border-box;
  text-align: right;
}

#order-button {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  background: #6DADAD;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
}

#product-dimension {
  position: absolute;
  bottom: 55px;
  left: 0;
  line-height: 20px;
  text-align: left;
  font-size: 14px;
  color: rgb(102, 102, 102);
}

#component-photo-panel-container {
  position: absolute;
  bottom: 0;
  right: 480px;
  left: 20px;
  height: 90px;
}

#component-photo-panel {
  max-width: 100%;
  display: inline-block;
  text-align: left;
  z-index: 200;
}

.component-summary {
  cursor: pointer;
}

.component-summary:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// App.vue?f3ef1e3c